import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, X } from 'lucide-react';
import api from '../../utils/api';

interface Testimonial {
  id: string;
  quote: string;
  attribution: string;
  context: string | null;
  isVisible: boolean;
  sortOrder: number;
  createdAt?: string;
}

const emptyDraft = { quote: '', attribution: '', context: '' };

const TestimonialsManager: React.FC = () => {
  const [rows, setRows] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ quote: string; attribution: string; context: string }>(emptyDraft);
  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/testimonials/all');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const beginEdit = (row: Testimonial) => {
    setEditingId(row.id);
    setEditDraft({
      quote: row.quote || '',
      attribution: row.attribution || '',
      context: row.context || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft);
  };

  const saveEdit = async (id: string) => {
    if (!editDraft.quote.trim() || !editDraft.attribution.trim()) {
      window.alert('Quote and attribution are required');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/testimonials/${id}`, {
        quote: editDraft.quote,
        attribution: editDraft.attribution,
        context: editDraft.context || null,
      });
      cancelEdit();
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to save testimonial');
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (row: Testimonial) => {
    try {
      await api.put(`/testimonials/${row.id}`, { isVisible: !row.isVisible });
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to update visibility');
    }
  };

  const handleDelete = async (row: Testimonial) => {
    if (!window.confirm(`Delete this testimonial from ${row.attribution || 'unknown'}? This cannot be undone.`)) return;
    try {
      await api.delete(`/testimonials/${row.id}`);
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to delete testimonial');
    }
  };

  const handleAdd = async () => {
    if (!addDraft.quote.trim() || !addDraft.attribution.trim()) {
      window.alert('Quote and attribution are required');
      return;
    }
    setBusy(true);
    try {
      await api.post('/testimonials', {
        quote: addDraft.quote,
        attribution: addDraft.attribution,
        context: addDraft.context || null,
        sortOrder: rows.length,
        isVisible: true,
      });
      setAddDraft(emptyDraft);
      setShowAdd(false);
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to add testimonial');
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= rows.length) return;
    const newOrder = [...rows];
    const [item] = newOrder.splice(index, 1);
    newOrder.splice(next, 0, item);
    setRows(newOrder);
    try {
      await api.post('/testimonials/reorder', { order: newOrder.map(r => r.id) });
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to reorder');
      await load();
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-h3 text-ink mb-2">Testimonials</h2>
          <p className="text-sm text-muted">Quotes from parents and students. Shown on the home page in the order below.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(s => !s)}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add testimonial</span>
        </button>
      </div>

      {showAdd && (
        <div className="card mb-6">
          <h3 className="font-serif text-lg text-ink mb-4">New testimonial</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-ink mb-1">Quote</label>
              <textarea
                className="input min-h-[100px]"
                value={addDraft.quote}
                onChange={e => setAddDraft(d => ({ ...d, quote: e.target.value }))}
                placeholder="What the parent or student said."
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-ink mb-1">Attribution</label>
                <input
                  className="input"
                  value={addDraft.attribution}
                  onChange={e => setAddDraft(d => ({ ...d, attribution: e.target.value }))}
                  placeholder="e.g. Year 6 parent"
                />
              </div>
              <div>
                <label className="block text-sm text-ink mb-1">Context (optional)</label>
                <input
                  className="input"
                  value={addDraft.context}
                  onChange={e => setAddDraft(d => ({ ...d, context: e.target.value }))}
                  placeholder="e.g. Sutton"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => { setShowAdd(false); setAddDraft(emptyDraft); }}
                className="btn btn-secondary"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                className="btn btn-primary"
                disabled={busy}
              >
                {busy ? 'Saving...' : 'Save testimonial'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted text-sm">Loading...</p>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <p className="text-muted text-sm">No testimonials yet. Add the first one above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row, i) => {
            const isEditing = editingId === row.id;
            return (
              <div key={row.id} className="card">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1 text-muted hover:text-ink disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === rows.length - 1}
                      className="p-1 text-muted hover:text-ink disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.isVisible}
                        onChange={() => toggleVisible(row)}
                      />
                      <span>Visible</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      className="p-1 text-muted hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-ink mb-1">Quote</label>
                      <textarea
                        className="input min-h-[100px]"
                        value={editDraft.quote}
                        onChange={e => setEditDraft(d => ({ ...d, quote: e.target.value }))}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-ink mb-1">Attribution</label>
                        <input
                          className="input"
                          value={editDraft.attribution}
                          onChange={e => setEditDraft(d => ({ ...d, attribution: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-ink mb-1">Context (optional)</label>
                        <input
                          className="input"
                          value={editDraft.context}
                          onChange={e => setEditDraft(d => ({ ...d, context: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="btn btn-secondary inline-flex items-center gap-2"
                        disabled={busy}
                      >
                        <X className="w-4 h-4" /> Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(row.id)}
                        className="btn btn-primary inline-flex items-center gap-2"
                        disabled={busy}
                      >
                        <Save className="w-4 h-4" />
                        {busy ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-ink whitespace-pre-wrap mb-2">"{row.quote}"</p>
                    <p className="text-sm text-muted">
                      {row.attribution}
                      {row.context ? ` · ${row.context}` : ''}
                    </p>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => beginEdit(row)}
                        className="btn btn-secondary text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager;
